import { CalendarScreenConfigSchema, type CalendarScreenConfig } from '@school-journey/shared-types'

import type { ScreenFeatureEntry } from './entry'

/**
 * Screen `calendar`.
 *
 * absentIsEmpty: the fleet-default SCREEN_CONFIGS row has no `calendar` block,
 * and only 4 of 33 school rows carry one (read-only production query,
 * `SystemConfiguration`, 2026-10-02).
 *
 * Fallback, for a school with no block:
 * - `enabled: true`, `access: '*'`: what the web calendar-source save assumes
 *   for a school with no calendar screen
 *   (`apps/web/src/lib/hooks/use-calendar-source.ts:76-79`). Mobile reads
 *   neither field for the calendar screen; both are required by the schema.
 * - `config.integration: 'blackbaud'`: the backend's answer when the field is
 *   absent (`apps/backend/src/services/platform-entities/calendar/resolve-calendar-source.ts:51`).
 *
 * `config.showCategoryBadges` is not in the fallback: absent reads as shown,
 * as the mobile event card reads it (`showsCategoryBadge`,
 * `apps/mobile/src/domains/calendar/model/category-badge.ts`).
 *
 * The first sparse save to a school with no block sends the fallback with the
 * change merged on top: the server merges the patch onto `{}` and parses the
 * whole config strictly, so a bare `{ config }` would be refused for missing
 * `enabled` and `access` (`use-screen-feature.ts`, the save path).
 */
export const calendar: ScreenFeatureEntry<'screen', 'calendar', CalendarScreenConfig> = {
  resource: 'screen',
  wireName: 'calendar',
  schema: CalendarScreenConfigSchema,
  // Sparse (U9a): merged inside the server's compare-and-swap, so concurrent sibling saves both land.
  writeMode: 'sparse',
  absentIsEmpty: true,
  fallback: {
    enabled: true,
    access: '*',
    config: { integration: 'blackbaud' }
  }
}
